import {
	ZodTypeAny,
	ZodObject,
	ZodArray,
	ZodUnion,
	ZodTuple,
	ZodSchema,
	ZodRecord,
	ZodMap,
	ZodEffects,
	ZodOptional,
	ZodNullable,
	ZodDefault,
} from 'zod';
import { createContext, useContext, useMemo } from 'react';
import { Path } from 'react-hook-form';


function unwrap(schema: ZodTypeAny): ZodTypeAny {
	while (true) {
		if (schema instanceof ZodEffects) {
			schema = schema._def.schema;
		} else if (schema instanceof ZodOptional) {
			schema = schema._def.innerType;
		} else if (schema instanceof ZodNullable) {
			schema = schema._def.innerType;
		} else if (schema instanceof ZodDefault) {
			schema = schema._def.innerType;
		} else {
			break;
		}
	}
	return schema;
}

export function getPartialSchema(schema: ZodTypeAny, path: string[]): ZodTypeAny {

	// Solo desenreda completamente al inicio, en el resto me quiero quedar con el esquema tal cual, tenga o no ZodEffects 
	// ZOptional, etc.
	let current: ZodTypeAny = unwrap(schema);

	for (let i = 0; i < path.length; i++) {

		const key = path[i];

		if (current instanceof ZodObject) {
			const next = current.shape[key];
			if (!next) {
				throw new Error(`No field "${key}" in object at path "${path.slice(0, i + 1).join('.')}"`);
			}
			current = next;
		} else if (current instanceof ZodArray) {
			if (!/^\d+$/.test(key)) {
				throw new Error(`"${key}" is not a valid array index at path "${path.slice(0, i + 1).join('.')}"`);
			}
			current = current.element;
		} else if (current instanceof ZodTuple) {
			const index = parseInt(key, 10);
			if (isNaN(index) || index >= current.items.length) {
				throw new Error(`"${key}" is not a valid tuple index at path "${path.slice(0, i + 1).join('.')}"`);
			}
			current = current.items[index];
		} else if (current instanceof ZodRecord || current instanceof ZodMap) {
			current = current.valueSchema;
		} else if (current instanceof ZodUnion) {

			// Buscar en la opción que tenga esa propiedad
			const matching = current._def.options
				.map((opt: any) => opt instanceof ZodObject && key in opt.shape ? opt : undefined)
				.find((opt: any) => opt);

			if (matching && matching instanceof ZodObject) {
				current = matching.shape[key];
			} else {
				throw new Error(`"${key}" is not a valid union at path "${path.slice(0, i + 1).join('.')}"`);
			}

		} else {
			throw new Error(`Cannot access "${key}" on non-object type (${current._def.typeName}) at "${path.slice(0, i + 1).join('.')}"`);
		}
	}

	return current;
}

export const FormSchemaContext = createContext<ZodSchema<any> | undefined>(undefined);

export function useFieldSchema<T>(path: Path<T>): ZodTypeAny {

	const context = useContext(FormSchemaContext);
	if (!context) {
		throw new Error('useFieldSchema debe usarse dentro de un <FormSchemaProvider>');
	}

	const schema = context as ZodSchema<T>;

	return useMemo(() => {

		const parts = path.split('.');
		const fieldSchema = getPartialSchema(schema, parts);

		if (fieldSchema === null)
			throw new Error(`Not field schema defined for ${path}`);

		return fieldSchema;

	}, [schema, path]);
}
